import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import {
  Col,
  Drawer,
  Row,
  Select,
} from 'antd';
import ExternalSignatureSigningOrder from './ExternalSignature/ExternalSignatureSigningOrder';
import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import EmailSubject from './EmailSubject';
import useFormEmailOptions from '../../common/hooks/useFormEmailOptions';
import EmailRecipientSelector from './selectors/EmailRecipientSelector';
import HoverHelp from '../../common/HoverHelp';

const NAME_COLUMN_DISPLAY_OPTIONS = [
  { value: 'email', label: "Signer's Email" },
  { value: 'name', label: "Signer's Name" },
];

export default function WorkflowExternalSignatureConfigureDrawer({
  id,
  visible,
  onClose,
  onSubmit,
  data,
  setDataMap,
  divisionId,
  divisionIds = [],
  sections = [],
  signers,
  setSigners,
  isV3Form,
}) {
  const {
    additionalOptions,
    subjectOptions,
  } = useFormEmailOptions(sections);

  const {
    additionalOptions: notificationOptions,
  } = useFormEmailOptions(sections, 'notification');

  const {
    errorEmails,
    nameColumnDisplay: initialNameColumnDisplay = 'email',
  } = data;

  const [nameColumnDisplay, setNameColumnDisplay] = useState(initialNameColumnDisplay);

  const updateEmails = useCallback((newSelected = []) => {
    setDataMap((dataMap) => {
      const {
        [id]: oldData = {},
      } = dataMap;
      return {
        ...dataMap,
        [id]: {
          ...oldData,
          errorEmails: newSelected,
        },
      };
    });
  }, [setDataMap]);

  const updateNameColumnDisplay = useCallback((newDisplay) => {
    setNameColumnDisplay(newDisplay);
    setDataMap((dataMap) => {
      const {
        [id]: oldData = {},
      } = dataMap;
      return {
        ...dataMap,
        [id]: {
          ...oldData,
          nameColumnDisplay: newDisplay,
        },
      };
    });
  }, [id, setDataMap]);

  return (
    <Drawer
      title="Configure Envelope Options"
      width={750}
      onClose={onClose}
      visible={visible}
      bodyStyle={{ paddingBottom: 60 }}
    >
      <Col>
        <EmailRecipientSelector
          sections={sections}
          initialSelected={errorEmails}
          divisionId={divisionId}
          divisionIds={divisionIds}
          onChange={updateEmails}
          additionalOptions={notificationOptions}
          text={
            (
              <>
                Select Error Notification Recipient(s):
                <HoverHelp
                  iconProps={{
                    style: { paddingTop: 4, paddingLeft: 5 },
                  }}
                  content={(
                    <div style={{ maxWidth: 300 }}>
                      Who will be notified if the envelope fails to create in Docusign.
                      <br />
                      The envelope may fail if
                      <ul>
                        <li>The PDF is larger than Docusign&apos;s limit of 32MB</li>
                        <li>The configured Signers or Subject are not accepted by Docusign</li>
                        <li>There is an internal issue connecting to Docusign</li>
                      </ul>
                    </div>
                  )}
                />
              </>
              )
            }
          showFormAuthor
          authorHelpText="The notification will be sent to the form's author"
          showCompanyOwner
          ownerHelpText="The notification will be sent to the company's owner"
        />
        <EmailSubject
          id={id}
          data={data}
          setDataMap={setDataMap}
          fields={subjectOptions}
          visible={visible}
        />
        {isV3Form && (
          <>
            <Row style={{ marginTop: 5 }}>
              Name Column Displays:
              <HoverHelp
                iconProps={{
                  style: { paddingTop: 4, paddingLeft: 5 },
                }}
                content={(
                  <div style={{ maxWidth: 300 }}>
                    How each signer is identified in the generated PDF, both in the
                    signature table and in the single signature block.
                    <br />
                    <br />
                    With <b>Signer&apos;s Name</b>, Docusign requires each signer to type their
                    full name before they can finish signing, and that name replaces their
                    email everywhere the signer is shown.
                  </div>
                )}
              />
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                value={nameColumnDisplay}
                onChange={updateNameColumnDisplay}
                options={NAME_COLUMN_DISPLAY_OPTIONS}
                style={{ width: '100%' }}
              />
            </Row>
          </>
        )}
        <Row>
          <ExternalSignatureSigningOrder
            divisionId={divisionId}
            divisionIds={divisionIds}
            sections={sections}
            id={id}
            setDataMap={setDataMap}
            additionalOptions={additionalOptions}
            signers={signers}
            setSigners={setSigners}
          />
        </Row>
      </Col>
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </Drawer>
  );
}

/* eslint-disable react/forbid-prop-types */
WorkflowExternalSignatureConfigureDrawer.propTypes = {
  data: PropTypes.shape({
    errorEmails: PropTypes.array,
    nameColumnDisplay: PropTypes.string,
  }).isRequired,
  divisionId: PropTypes.string,
  divisionIds: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  sections: PropTypes.array,
  setDataMap: PropTypes.func.isRequired,
  visible: PropTypes.bool,
  signers: PropTypes.array,
  setSigners: PropTypes.func.isRequired,
  isV3Form: PropTypes.bool,
};

WorkflowExternalSignatureConfigureDrawer.defaultProps = {
  sections: [],
  divisionId: null,
  divisionIds: [],
  visible: false,
  signers: [],
  isV3Form: false,
};
